/**
 * Runtime environment variable reader.
 *
 * Priority:
 *   1. window.__ENV__  — injected by nginx entrypoint.sh from K8s ConfigMap at container startup
 *   2. import.meta.env — Vite build-time env vars (local development via .env)
 *
 * This allows a single Docker image to run in any environment without rebuilding.
 */

declare global {
  interface Window {
    __ENV__?: {
      API_URL?: string;
    };
  }
}

const PLACEHOLDER_PATTERN = /PENDING_/i;

const API_HOST_MAP: Record<string, string> = {
  'dev.ti-ecoa.com': 'https://dev-api.ti-ecoa.com',
  'dev-patient.ti-ecoa.com': 'https://dev-api.ti-ecoa.com',
  'edu.ti-ecoa.com': 'https://edu-api.ti-ecoa.com',
  'edu-patient.ti-ecoa.com': 'https://edu-api.ti-ecoa.com',
  'uat.ti-ecoa.com': 'https://uat-api.ti-ecoa.com',
  'uat-patient.ti-ecoa.com': 'https://uat-api.ti-ecoa.com',
  'dev.ecoa.ai-do.co.kr': 'https://dev-api.ecoa.ai-do.co.kr',
  'dev-patient.ecoa.ai-do.co.kr': 'https://dev-api.ecoa.ai-do.co.kr',
  'edu.ecoa.ai-do.co.kr': 'https://edu-api.ecoa.ai-do.co.kr',
  'edu-patient.ecoa.ai-do.co.kr': 'https://edu-api.ecoa.ai-do.co.kr',
  'uat2.ecoa.ai-do.co.kr': 'https://api2.ecoa.ai-do.co.kr',
  'ecoa2.ai-do.co.kr': 'https://api2.ecoa.ai-do.co.kr',
};

function normalizeUrl(value: string): string {
  return value.trim().replace(/\/$/, '');
}

function isUsableRuntimeUrl(value: string | undefined): value is string {
  if (!value) return false;
  const normalized = normalizeUrl(value);
  if (!normalized) return false;
  if (PLACEHOLDER_PATTERN.test(normalized)) return false;
  if (window.location.protocol === 'https:' && normalized.startsWith('http://')) return false;
  return true;
}

export function getEnv(key: 'API_URL'): string {
  const runtimeValue = window.__ENV__?.[key];
  if (isUsableRuntimeUrl(runtimeValue)) {
    return normalizeUrl(runtimeValue);
  }

  const buildValue = import.meta.env[`VITE_${key}`];
  if (isUsableRuntimeUrl(buildValue)) {
    return normalizeUrl(buildValue);
  }

  const mapped = API_HOST_MAP[window.location.hostname];
  if (mapped) {
    return mapped;
  }

  return '';
}
